import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  InputAdornment,
  Slider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ClientDashboardLayout from './ClientDashboardLayout';
import { PrimaryButton, SecondaryButton } from '../designSystem';
import { useParams } from 'react-router-dom';
import { getAllocation, patchAllocation } from '../../api/targetAllocation';

const allocationLabels = [
  {
    key: 'team',
    label: 'Team Costs',
    color: '#536DEC',
    description: 'Salaries, benefits, contractors, and staffing overhead',
    image: "/users.svg"
  },
  {
    key: 'ops',
    label: 'Operations Costs',
    color: '#FFB300',
    description: 'Rent, utilities, insurance, software, and office expenses',
    image: "/ops.svg"
  },
  {
    key: 'products',
    label: 'Products Costs',
    color: '#00ACC1',
    description: 'Inventory, supplies, equipment, and COGS',
    image: "/stall.svg"
  },
  {
    key: 'marketing',
    label: 'Marketing Costs',
    color: '#9C27B0',
    description: 'Advertising, campaigns, sponsorships, and outreach',
    image: "/costs.svg"
  },
];

const defaultAllocations = {
  team: 0,
  ops: 0,
  products: 0,
  marketing: 0,
};

const TargetAllocation = () => {
  const [allocations, setAllocations] = useState(defaultAllocations);
  const [savedAllocations, setSavedAllocations] = useState(defaultAllocations);
  const [showSaveError, setShowSaveError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const { customer_id } = useParams();
  const [customerId, setCustomerId] = useState(customer_id || null);

  useEffect(() => {
    const fetchAllocation = async () => {
      try {
        const data = await getAllocation(customerId);
        setAllocations({
          team: parseFloat(data?.team_pct ?? 0),
          ops: parseFloat(data?.ops_pct ?? 0),
          products: parseFloat(data?.products_pct ?? 0),
          marketing: parseFloat(data?.marketing_pct ?? 0),
          profit: parseFloat(data?.profit_pct ?? 0),
        });
        setSavedAllocations({
          team: parseFloat(data?.team_pct ?? 0),
          ops: parseFloat(data?.ops_pct ?? 0),
          products: parseFloat(data?.products_pct ?? 0),
          marketing: parseFloat(data?.marketing_pct ?? 0),
          profit: parseFloat(data?.profit_pct ?? 0),
        });
      } catch (err) {
        console.error("Failed to load allocations", err);
      }
    };
    if (!customerId) return;
    fetchAllocation();
  }, [customerId]);

  const handleChange = (key, value) => {
    const newValue = Number(value) || 0;
    setAllocations(prev => ({
      ...prev,
      [key]: Math.min(Math.max(newValue, 0), 100)
    }));
  };

  const totalWithoutProfit = allocationLabels.reduce(
    (sum, { key }) => sum + Number(allocations[key] || 0),
    0
  );

  const profit = Math.max(100 - totalWithoutProfit, 0);
  const totalWithProfit = totalWithoutProfit + profit;
  const over = totalWithoutProfit > 100;

  const segments = [
    ...allocationLabels.map(item => ({
      key: item.key,
      label: item.label,
      value: allocations[item.key],
      color: item.color,
    })),
    { key: 'profit', label: 'Profit', value: profit, color: '#4CAF50' },
  ];

  const rows = [...allocationLabels];

  const handleCancel = () => {
    setAllocations(savedAllocations);
    setShowSaveError(false);
  };

  const handleSave = async () => {
    if (!customerId) return;
    if (over) {
      setShowSaveError(true);
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        team_pct: allocations.team,
        ops_pct: allocations.ops,
        products_pct: allocations.products,
        marketing_pct: allocations.marketing,
        profit_pct: profit,
      };

      await patchAllocation(customerId, payload);
      setSavedAllocations(allocations);
      setShowSaveError(false);
      setSnackbarMsg('Targets saved successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Save failed", err);
      setSnackbarMsg('Failed to save targets. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!over && totalWithoutProfit === 100) {
      setShowSaveError(false);
    }
  }, [over, totalWithoutProfit]);

  const hasChanges = () => {
    return allocationLabels.some(label => {
      const key = label.key;
      return allocations[key] !== savedAllocations[key];
    });
  };

  return (
    <ClientDashboardLayout title={null} contentMaxWidth="100%" contentAlign="left">
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="h5"
            sx={{ fontSize: "24px", fontWeight: 600 }}
          >
            Targets
          </Typography>

          <Box sx={{ flex: 1 }} />
          {hasChanges() && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <SecondaryButton onClick={handleCancel}>
                Cancel
              </SecondaryButton>

              <PrimaryButton
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </PrimaryButton>
            </Box>)}
        </Box>

        <Divider />

        {(showSaveError) && (
          <Alert
            severity="error"
            sx={{ mb: 2, alignItems: "center" }}
            icon={
              <Box
                sx={{
                  backgroundColor: "#D32F2F1A",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src="/ErrorTriangle.svg"
                  alt="error"
                  sx={{ width: 20, height: 20 }}
                />
              </Box>
            }
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography fontWeight={600}>
                Cannot save — allocations must total exactly 100%.
              </Typography>
              <Typography variant="body2">
                Your current total is {totalWithoutProfit.toFixed(1)}%.
                {over && (
                  <> Reduce allocations by {(totalWithoutProfit - 100).toFixed(0)}% to reach 100%.</>
                )}
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Allocation Overview */}
        <Box sx={{ p: "16px", borderRadius: "16px", backgroundColor: '#fff', border: "1px solid #00000014" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ mr: 1, fontSize: "18px", fontWeight: 600 }}>
              Income Allocation
            </Typography>

            <Chip
              icon={
                over ? (
                  <Box component="img" src="/ErrorTriangle.svg" sx={{ width: 14, height: 14 }} />
                ) : (
                  <Box component="img" src="/BsCheck2Circle.svg" sx={{ width: 14, height: 14 }} />
                )
              }
              label={
                over
                  ? `${totalWithoutProfit}% (${(totalWithoutProfit - 100).toFixed(0)}% over)`
                  : `${totalWithProfit}%`
              }
              sx={{
                fontWeight: 500,
                backgroundColor: over ? '#FEEBEE' : '#E8F5E9',
                color: over ? 'black' : '#2E7D32',
              }}
            />

            <Box sx={{ flex: 1 }} />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {segments.map(seg => (
                <Box key={seg.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: seg.color,
                    }}
                  />
                  <Typography variant="body2">{seg.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              height: 16,
              width: '100%',
              borderRadius: 6,
              overflow: 'hidden',
              gap: '2px',
            }}
          >
            {segments.map(seg =>
              seg.value > 0 && (
                <Box
                  key={seg.key}
                  sx={{ width: `${seg.value}%`, backgroundColor: seg.color }}
                />
              )
            )}
          </Box>

          <Box sx={{ display: 'flex', mt: 1 }}>
            {segments.map(seg =>
              seg.value > 0 && (
                <Box
                  key={seg.key}
                  sx={{ width: `${seg.value}%`, textAlign: 'center', fontSize: '0.8rem' }}
                >
                  {seg.value}%
                </Box>
              )
            )}
          </Box>
        </Box>

        <Box sx={{ borderRadius: "16px", backgroundColor: '#fff', border: "1px solid #00000014" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#EEF0FD' }}>
                  <TableCell sx={{ fontSize: "18px", fontWeight: 600 }}>
                    Income Allocation
                  </TableCell>
                  <TableCell />
                  <TableCell sx={{ fontSize: "18px", fontWeight: 600 }}>
                    Percentage
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.key}>
                    <TableCell sx={{ width: '45%' }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "44px",
                            height: "44px",
                            bgcolor: "#2632380F",
                            borderRadius: "8px"
                          }}
                        >
                          <Box
                            component="img"
                            src={row.image}
                            alt={row.label}
                            sx={{ width: "22px", height: "22px" }}
                          />
                        </Box>

                        <Box>
                          <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                            {row.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "14px" }}>
                            {row.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ width: '45%' }}>
                      <Slider
                        value={allocations[row.key]}
                        onChange={(e, val) => handleChange(row.key, val)}
                        step={1}
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                      />
                    </TableCell>

                    <TableCell sx={{ width: '10%' }}>
                      <TextField
                        size="small"
                        type="text"
                        value={allocations[row.key].toString()}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAllocations(prev => ({
                            ...prev,
                            [row.key]: val === "" ? "" : val.replace(/[^\d]/g, ""),
                          }));
                        }}
                        onBlur={() => {

                          if (allocations[row.key] === "") {
                            setAllocations(prev => ({
                              ...prev,
                              [row.key]: 0,
                            }));
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </ClientDashboardLayout>
  );
};

export default TargetAllocation;